"use client";

import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithRedirect,
  signOut,
  type User,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  setRuntimeModelCreditCosts,
  type ModelCreditCostConfig,
} from "@reelmagic/domain";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import type { ReactNode } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import {
  getFirebaseClientServices,
  getDefaultFirebaseClientServices,
  endImpersonationFirebaseSession,
  isFirebaseWebConfigReady,
  restoreImpersonationFirebaseSession,
  startImpersonationFirebaseSession,
  type ImpersonationSession,
} from "../../lib/firebase/client";
import { reportCaughtError } from "../../lib/sentry/report-caught-error";

const isVercelProduction =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "production";
const shouldEnablePostHog =
  Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) && isVercelProduction;

function isIndexedDbAvailable() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return typeof window.indexedDB !== "undefined";
  } catch {
    return false;
  }
}

type ClientConfigurationStatus = "pending" | "ready" | "unavailable";

function subscribeToClientConfigurationStatus() {
  return () => undefined;
}

function getClientConfigurationStatus(): ClientConfigurationStatus {
  return isFirebaseWebConfigReady() && isIndexedDbAvailable()
    ? "ready"
    : "unavailable";
}

function getServerConfigurationStatus(): ClientConfigurationStatus {
  return "pending";
}

interface AuthContextValue {
  endImpersonation(): Promise<void>;
  hasCheckedConfiguration: boolean;
  impersonation: ImpersonationSession | null;
  isConfigured: boolean;
  isLoading: boolean;
  modelCreditCostsVersion: number;
  sendEmailSignInCode(
    email: string,
    nextPath: string,
    turnstileToken: string,
    authAttemptId?: string,
  ): Promise<{ authAttemptId: string; expiresAt: string }>;
  signInWithGoogle(): Promise<void>;
  signOutUser(): Promise<void>;
  verifyEmailSignInCode(input: {
    authAttemptId: string;
    code: string;
    email: string;
  }): Promise<void>;
  user: User | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function FirebaseAuthProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const configurationStatus = useSyncExternalStore(
    subscribeToClientConfigurationStatus,
    getClientConfigurationStatus,
    getServerConfigurationStatus,
  );
  const hasCheckedConfiguration = configurationStatus !== "pending";
  const isConfigured = configurationStatus === "ready";
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationSession | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [modelCreditCostsVersion, setModelCreditCostsVersion] = useState(0);
  const isLoading = !hasCheckedConfiguration || (isConfigured && isAuthLoading);

  useEffect(() => {
    if (!hasCheckedConfiguration || !isConfigured) {
      return;
    }

    const { auth: defaultAuth, functions: defaultFunctions } =
      getDefaultFirebaseClientServices();
    void getRedirectResult(defaultAuth).catch((error: unknown) => {
      reportCaughtError(error, {
        expectedCodes: [
          "auth/cancelled-popup-request",
          "auth/popup-closed-by-user",
        ],
        operation: "auth.get_redirect_result",
      });
    });
    let unsubscribe: () => void = () => undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    async function initializeAuthSession() {
      let auth = defaultAuth;
      let activeSession: ImpersonationSession | null = null;
      const hash = new URLSearchParams(window.location.hash.slice(1));
      const exchangeCode = hash.get("impersonate");

      if (exchangeCode) {
        hash.delete("impersonate");
        const nextHash = hash.toString();
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ""}`,
        );
        const exchange = httpsCallable<
          { code: string },
          ImpersonationSession & { customToken: string }
        >(defaultFunctions, "exchangeImpersonationSession");
        const result = await exchange({ code: exchangeCode });
        const { customToken, ...session } = result.data;
        activeSession = session;
        auth = await startImpersonationFirebaseSession(session, customToken);
      } else {
        const restored = await restoreImpersonationFirebaseSession();
        if (restored) {
          activeSession = restored.session;
          auth = restored.auth;
        }
      }

      if (cancelled) return;
      setImpersonation(activeSession);
      Sentry.setTag("impersonating", activeSession ? "true" : "false");
      Sentry.setTag("impersonation_session_id", activeSession?.sessionId ?? undefined);
      Sentry.setTag("impersonation_target_uid", activeSession?.targetUid ?? undefined);
      if (activeSession) {
        const expiresInMs = Math.max(0, Date.parse(activeSession.expiresAt) - Date.now());
        expiryTimer = setTimeout(() => {
          void endImpersonationFirebaseSession().finally(() => {
            window.location.assign(activeSession?.returnUrl ?? "/login");
          });
        }, expiresInMs);
      }

      unsubscribe = onAuthStateChanged(auth, (nextUser) => {
        setUser(nextUser);
        setIsAuthLoading(false);

        if (!shouldEnablePostHog) return;
        if (nextUser && activeSession) {
          posthog.identify(activeSession.actorUid, {
            email: activeSession.actorEmail,
            impersonating: true,
          });
          posthog.register({
            impersonation_session_id: activeSession.sessionId,
            impersonation_target_uid: activeSession.targetUid,
          });
        } else if (nextUser) {
          posthog.unregister("impersonation_session_id");
          posthog.unregister("impersonation_target_uid");
          posthog.identify(nextUser.uid, {
            email: nextUser.email ?? undefined,
            firebase_provider: nextUser.providerData[0]?.providerId,
          });
        } else {
          posthog.reset();
        }
      });
    }

    void initializeAuthSession().catch(async (error: unknown) => {
      reportCaughtError(error, {
        operation: "auth.initialize_session",
      });
      await endImpersonationFirebaseSession().catch(() => undefined);
      if (!cancelled) {
        setImpersonation(null);
        setUser(null);
        setIsAuthLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [hasCheckedConfiguration, isConfigured]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setRuntimeModelCreditCosts(null);
      return;
    }

    const { functions } = getFirebaseClientServices();
    const getModelCreditCosts = httpsCallable<undefined, { config: ModelCreditCostConfig }>(
      functions,
      "getModelCreditCosts",
    );
    void getModelCreditCosts()
      .then((result) => {
        if (cancelled) return;
        setRuntimeModelCreditCosts(result.data.config);
        setModelCreditCostsVersion((version) => version + 1);
      })
      .catch((error: unknown) => {
        reportCaughtError(error, { operation: "billing.load_model_credit_costs" });
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  async function endImpersonation() {
    if (!impersonation) return;
    const returnUrl = impersonation.returnUrl;
    try {
      const { functions } = getFirebaseClientServices();
      const endSession = httpsCallable<Record<string, never>, { endedAt: string }>(
        functions,
        "endImpersonationSession",
      );
      await endSession({});
    } finally {
      await endImpersonationFirebaseSession();
      Sentry.setTag("impersonating", "false");
      Sentry.setTag("impersonation_session_id", undefined);
      Sentry.setTag("impersonation_target_uid", undefined);
      setImpersonation(null);
      setUser(null);
      window.location.assign(returnUrl);
    }
  }

  async function sendEmailSignInCode(
    email: string,
    nextPath: string,
    turnstileToken: string,
    authAttemptId?: string,
  ) {
    const { functions } = getFirebaseClientServices();
    const sendPasswordlessSignInCode = httpsCallable<
      {
        authAttemptId?: string;
        email: string;
        nextPath: string;
        origin: string;
        turnstileToken: string;
      },
      { authAttemptId: string; expiresAt: string; sent: boolean }
    >(functions, "sendPasswordlessSignInCode");

    const result = await sendPasswordlessSignInCode({
      ...(authAttemptId ? { authAttemptId } : {}),
      email,
      nextPath,
      origin: window.location.origin,
      turnstileToken,
    });

    return {
      authAttemptId: result.data.authAttemptId,
      expiresAt: result.data.expiresAt,
    };
  }

  async function verifyEmailSignInCode(input: {
    authAttemptId: string;
    code: string;
    email: string;
  }) {
    const { auth, functions } = getFirebaseClientServices();
    const verifyPasswordlessSignInCode = httpsCallable<
      {
        authAttemptId: string;
        code: string;
        email: string;
      },
      { customToken: string }
    >(functions, "verifyPasswordlessSignInCode");

    const result = await verifyPasswordlessSignInCode(input);
    const customToken = result.data.customToken;

    await signInWithCustomToken(auth, customToken);
  }

  async function signInWithGoogle() {
    const { auth } = getFirebaseClientServices();
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  }

  async function signOutUser() {
    if (impersonation) {
      await endImpersonation();
      return;
    }
    const { auth } = getFirebaseClientServices();
    await signOut(auth);
  }

  return (
    <AuthContext.Provider
      value={{
        endImpersonation,
        hasCheckedConfiguration,
        impersonation,
        isConfigured,
        isLoading,
        modelCreditCostsVersion,
        sendEmailSignInCode,
        signInWithGoogle,
        signOutUser,
        verifyEmailSignInCode,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useFirebaseAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useFirebaseAuth must be used inside FirebaseAuthProvider.");
  }

  return value;
}
